/**
 * Goals Type Definitions
 * BP-06-03: Care Team & Goals Frontend
 */

// ============================================================================
// Enums
// ============================================================================

export type GoalType = 
  | 'iep'
  | 'therapy'
  | 'developmental'
  | 'behavioral'
  | 'academic'
  | 'other';

export type GoalStatus = 
  | 'not_started'
  | 'in_progress'
  | 'achieved'
  | 'discontinued';

export type GoalMeasurementType = 
  | 'percentage'
  | 'count'
  | 'rating'
  | 'yes_no'
  | 'custom';

export type GoalPriority = 'high' | 'medium' | 'low';

export type ReviewFrequency = 'weekly' | 'monthly' | 'quarterly';

// ============================================================================
// Core Types
// ============================================================================

export interface Goal {
  id: string;
  childId: string;
  createdById: string;
  title: string;
  description: string | null;
  goalType: GoalType;
  category: string | null;
  relatedDiagnosisId: string | null;
  careTeamMemberId: string | null;
  careTeamMember?: {
    id: string;
    name: string;
    title: string | null;
  } | null;
  startDate: string | null;
  targetDate: string | null;
  achievedDate: string | null;
  status: GoalStatus;
  progressPercentage: number;
  measurementType: GoalMeasurementType;
  baselineValue: string | null;
  targetValue: string | null;
  currentValue: string | null;
  isIepGoal: boolean;
  iepArea: string | null;
  reviewFrequency: ReviewFrequency | null;
  nextReviewDate: string | null;
  priority: GoalPriority;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GoalProgressEntry {
  id: string;
  goalId: string;
  enteredById: string;
  enteredBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  entryDate: string;
  value: string;
  percentage: number;
  notes: string | null;
  attachments: GoalAttachment[];
  sessionType: string | null;
  createdAt: string;
}

export interface GoalAttachment {
  id: string;
  name: string;
  url: string;
  type: string;
}

export interface GoalSummary {
  totalGoals: number;
  byStatus: Record<GoalStatus, number>;
  byType: Record<GoalType, number>;
  averageProgress: number;
  achievedThisMonth: number;
  upcomingReviews: number;
  overdue: number;
}

export interface IEPGoalReport {
  childId: string;
  childName: string;
  reportDate: string;
  goals: IEPGoalDetail[];
  summary: {
    totalGoals: number;
    onTrack: number;
    needsAttention: number;
    achieved: number;
  };
}

export interface IEPGoalDetail {
  id: string;
  title: string;
  iepArea: string | null;
  status: GoalStatus;
  progressPercentage: number;
  targetDate: string | null;
  measurementType: GoalMeasurementType;
  baselineValue: string | null;
  currentValue: string | null;
  targetValue: string | null;
  recentProgress: GoalProgressEntry[];
  careTeamMemberName: string | null;
  notes: string | null;
}

// ============================================================================
// Request/Response Types
// ============================================================================

export interface CreateGoalRequest {
  title: string;
  description?: string | null;
  goalType: GoalType;
  category?: string | null;
  relatedDiagnosisId?: string | null;
  careTeamMemberId?: string | null;
  startDate?: string | null;
  targetDate?: string | null;
  measurementType: GoalMeasurementType;
  baselineValue?: string | null;
  targetValue?: string | null;
  isIepGoal?: boolean;
  iepArea?: string | null;
  reviewFrequency?: ReviewFrequency | null;
  priority?: GoalPriority;
  notes?: string | null;
}

export interface UpdateGoalRequest {
  title?: string;
  description?: string | null;
  goalType?: GoalType;
  category?: string | null;
  relatedDiagnosisId?: string | null;
  careTeamMemberId?: string | null;
  startDate?: string | null;
  targetDate?: string | null;
  status?: GoalStatus;
  progressPercentage?: number;
  measurementType?: GoalMeasurementType;
  baselineValue?: string | null;
  targetValue?: string | null;
  currentValue?: string | null;
  isIepGoal?: boolean;
  iepArea?: string | null;
  reviewFrequency?: ReviewFrequency | null;
  nextReviewDate?: string | null;
  priority?: GoalPriority;
  notes?: string | null;
}

export interface CreateProgressEntryRequest {
  entryDate: string;
  value: string;
  percentage: number;
  notes?: string | null;
  sessionType?: string | null;
  attachments?: File[];
}

export interface UpdateProgressEntryRequest {
  entryDate?: string;
  value?: string;
  percentage?: number;
  notes?: string | null;
  sessionType?: string | null;
}

export interface GoalsListResponse {
  items: Goal[];
  total: number;
}

export interface GoalProgressResponse {
  items: GoalProgressEntry[];
  total: number;
}

// ============================================================================
// Filter Types
// ============================================================================

export interface GoalFilters {
  goalType?: GoalType;
  status?: GoalStatus;
  category?: string;
  priority?: GoalPriority;
  isIepGoal?: boolean;
  careTeamMemberId?: string;
  search?: string;
  startDateFrom?: string;
  startDateTo?: string;
  targetDateFrom?: string;
  targetDateTo?: string;
}

export interface GoalSortOptions {
  field: 'title' | 'targetDate' | 'progressPercentage' | 'createdAt' | 'priority';
  direction: 'asc' | 'desc';
}

// ============================================================================
// Export Types
// ============================================================================

export interface GoalsExportOptions {
  format: 'csv' | 'pdf';
  goalType?: GoalType;
  status?: GoalStatus;
  includeProgress?: boolean;
}

// ============================================================================
// Form Types
// ============================================================================

export interface GoalFormData {
  title: string;
  description: string;
  goalType: GoalType;
  category: string;
  relatedDiagnosisId: string;
  careTeamMemberId: string;
  startDate: string;
  targetDate: string;
  measurementType: GoalMeasurementType;
  baselineValue: string;
  targetValue: string;
  isIepGoal: boolean;
  iepArea: string;
  reviewFrequency: ReviewFrequency | '';
  priority: GoalPriority;
  notes: string;
}

export interface ProgressEntryFormData {
  entryDate: string;
  value: string;
  percentage: number;
  notes: string;
  sessionType: string;
}

// ============================================================================
// UI State Types
// ============================================================================

export interface GoalsUIState {
  viewMode: 'grid' | 'list';
  filters: GoalFilters;
  sort: GoalSortOptions;
  selectedGoalId: string | null;
  isFormOpen: boolean;
  editingGoalId: string | null;
  isProgressFormOpen: boolean;
}

// ============================================================================
// Chart Types
// ============================================================================

export interface ProgressChartData {
  date: string;
  value: number;
  percentage: number;
  label: string;
}

// ============================================================================
// Constants
// ============================================================================

export const GOAL_TYPE_LABELS: Record<GoalType, string> = {
  iep: 'IEP Goal',
  therapy: 'Therapy Goal',
  developmental: 'Developmental',
  behavioral: 'Behavioral',
  academic: 'Academic',
  other: 'Other',
};

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  achieved: 'Achieved',
  discontinued: 'Discontinued',
};

export const GOAL_STATUS_COLORS: Record<GoalStatus, string> = {
  not_started: 'gray',
  in_progress: 'sage',
  achieved: 'success',
  discontinued: 'terracotta',
};

export const PRIORITY_LABELS: Record<GoalPriority, string> = {
  high: 'High Priority',
  medium: 'Medium Priority',
  low: 'Low Priority',
};

export const MEASUREMENT_TYPE_LABELS: Record<GoalMeasurementType, string> = {
  percentage: 'Percentage (0-100%)',
  count: 'Count/Frequency',
  rating: 'Rating Scale',
  yes_no: 'Yes/No',
  custom: 'Custom Metric',
};

export const IEP_AREAS = [
  'Communication',
  'Social/Emotional',
  'Motor Skills',
  'Cognitive',
  'Adaptive Behavior',
  'Academic - Reading',
  'Academic - Writing',
  'Academic - Math',
  'Transition',
  'Other',
];

export const GOAL_CATEGORIES = [
  'Motor - Gross Motor',
  'Motor - Fine Motor',
  'Communication - Expressive',
  'Communication - Receptive',
  'Social Skills',
  'Self-Care',
  'Cognitive',
  'Behavioral',
  'Academic',
  'Play Skills',
  'Sensory',
  'Feeding',
  'Other',
];
